import type { StorydocFlatContentLayers, StoryYjsOperation } from "./schema";
import type { EntityType, FlatContentLayersType } from "@config";
import type { MoveInCellElementPayload } from "@store/story/utils/apply-in-cell-element-move";
import { normalizedFromNestedStructured } from "@/shared/utils/normalizer";

type MappingContext = {
  flatContentLayers?: FlatContentLayersType | Record<string, EntityType>;
  storySettings?: Record<string, unknown>;
};

type EventPayload = Record<string, any>;

// Maps a broadcast editor event to the Yjs op(s) that apply it to the shared
// Y.Doc. Note: undo/redo are NOT mapped here — in a collaborative room they are
// handled by the Yjs UndoManager (see undo-manager.ts), which computes inverses
// on the CRDT directly. The old handle-undo/handle-redo mapping was removed.
export const mapLiveblocksEventToYjsOperation = (
  event: string,
  payload: EventPayload,
  context: MappingContext = {},
): StoryYjsOperation | StoryYjsOperation[] | null => {
  if (event === "edit") return mapEdit(payload);
  if (event === "edit-many") return mapEditMany(payload);
  if (event === "delete") return mapDelete(payload);
  if (event === "create-entity") return mapCreate(payload, context);
  if (event === "reorder-entities") return mapReorder(payload, context);
  if (event === "update-story") return mapUpdateStory(payload);
  if (event === "update-story-settings") return mapUpdateStorySettings(payload);
  if (event === "set-version-data") return mapSetVersionData(payload);
  if (event === "design-system" || event === "update-design-system") {
    return mapDesignSystem(payload);
  }
  if (event === "restore-from-revision") return mapRestore(payload);
  if (event === "handle-colors") return mapCustomColors(payload, context);
  if (event === "handle-anchors") return mapAnchors(payload, context);
  if (event === "change-entity-layout") return mapChangeLayout(payload);
  return null;
};

function mapEdit(
  payload: EventPayload,
): StoryYjsOperation | StoryYjsOperation[] | null {
  if (!payload.id) return null;
  const editOp: StoryYjsOperation = {
    type: "edit-entity",
    id: payload.id,
    data: payload.data || {},
    full: payload.full,
    children: payload.children,
  };
  // Orphan-prune invariant (tables 2.0 in-cell elements): the tableData
  // rewrite removed in-cell children in the same reducer pass. Without delete
  // ops the Y.Doc keeps the orphans and the next reconcile resurrects them.
  const prunedIds = Array.isArray(payload.prunedChildIds)
    ? (payload.prunedChildIds as string[])
    : [];
  if (!prunedIds.length) return editOp;
  return [
    editOp,
    ...prunedIds.map((id) => ({ type: "delete-entity" as const, id })),
  ];
}

// editManyEntities patches settings on a batch of entities at once (e.g. a
// whole slide's translations). Emit one edit-entity op per entity; the bridge
// applies the array atomically in a single Y.Doc transaction.
function mapEditMany(payload: EventPayload): StoryYjsOperation[] | null {
  const data = payload.data;
  if (!data || typeof data !== "object") return null;
  const ops = Object.entries(
    data as Record<string, Record<string, unknown>>,
  ).map(([id, settings]) => ({
    type: "edit-entity" as const,
    id,
    data: settings || {},
  }));
  return ops.length ? ops : null;
}

function mapDelete(payload: EventPayload): StoryYjsOperation | null {
  if (!payload.id) return null;
  return { type: "delete-entity", id: payload.id };
}

function mapCreate(
  payload: EventPayload,
  context: MappingContext,
): StoryYjsOperation | StoryYjsOperation[] | null {
  if (payload.feature === "insert-into-cell") {
    return mapInsertIntoCell(payload);
  }
  if (payload.feature === "move-in-cell-element") {
    return mapMoveInCellElement(payload, context);
  }
  if (payload.feature === "paste-cells") {
    return mapPasteCells(payload);
  }
  const root =
    payload.broadcastPayload?.root ||
    payload.entities?.[payload.entityEntryRootId];
  const entities = payload.broadcastPayload?.entities || payload.entities;
  const parentId = root?.parentId || payload.parentId;

  if (!root || !entities || !parentId) return null;
  return {
    type: "create-entity",
    entity: root,
    entities,
    parentId,
    index: payload.index,
  };
}

// Tables 2.0 in-cell insert: one create op per sibling root (the doc positions
// each at index+i via fractional order keys, all in one transaction) plus an
// edit op carrying the post-patch tableData when the insert rewrote the cell's
// text (the payload value is used — Redux context would be pre-mutation).
function mapInsertIntoCell(payload: EventPayload): StoryYjsOperation[] | null {
  const { entities, rootIds, tableEntityId } = payload;
  if (
    !entities ||
    !Array.isArray(rootIds) ||
    !rootIds.length ||
    !tableEntityId
  ) {
    return null;
  }
  const baseIndex =
    typeof payload.index === "number" ? payload.index : undefined;
  const ops: StoryYjsOperation[] = [];
  (rootIds as string[]).forEach((rootId, i) => {
    const root = entities[rootId];
    if (!root) return;
    ops.push({
      type: "create-entity",
      entity: root,
      entities: collectPayloadSubtree(rootId, entities),
      parentId: tableEntityId,
      index: baseIndex === undefined ? undefined : baseIndex + i,
    });
  });
  if (payload.tableData) {
    ops.push({
      type: "edit-entity",
      id: tableEntityId,
      data: { tableData: payload.tableData },
    });
  }
  return ops.length ? ops : null;
}

// Root + transitive childIds descendants, restricted to the broadcast payload.
function collectPayloadSubtree(
  rootId: string,
  entities: Record<string, EntityType>,
): Record<string, EntityType> {
  const out: Record<string, EntityType> = {};
  const stack = [rootId];
  while (stack.length) {
    const id = stack.pop() as string;
    if (out[id]) continue;
    const entity = entities[id];
    if (!entity) continue;
    out[id] = entity;
    entity.childIds?.forEach((childId) => stack.push(childId));
  }
  return out;
}

// Tables 2.0 clipboard paste: one create op per pasted subtree root plus one
// edit op carrying the complete post-paste grid fields (payload values — the
// acting peer's Redux is already mutated, receivers' is not).
function mapPasteCells(payload: EventPayload): StoryYjsOperation[] | null {
  const { entities, rootIds, tableEntityId } = payload;
  if (!tableEntityId || !Array.isArray(rootIds)) return null;
  const ops: StoryYjsOperation[] = [];
  (rootIds as string[]).forEach((rootId) => {
    const root = entities?.[rootId];
    if (!root) return;
    ops.push({
      type: "create-entity",
      entity: root,
      entities: collectPayloadSubtree(rootId, entities),
      parentId: tableEntityId,
    });
  });
  ops.push({
    type: "edit-entity",
    id: tableEntityId,
    data: {
      tableData: payload.tableData,
      tableCols: payload.tableCols,
      tableRows: payload.tableRows,
      tableColWidths: payload.tableColWidths,
      tableRowHeights: payload.tableRowHeights,
    },
  });
  // Span-replace (payload.cells.length > 1): the target cells' pre-existing
  // in-cell subtrees were discarded locally. Without delete ops the Y.Doc
  // keeps them and the next reconcile resurrects the replaced cells' elements.
  const removedIds = Array.isArray(payload.removeRootIds)
    ? (payload.removeRootIds as string[])
    : [];
  removedIds.forEach((id) => ops.push({ type: "delete-entity", id }));
  // Orphan-prune invariant (tables 2.0 in-cell elements): the paste's tableData
  // rewrite may have removed in-cell children in the same reducer pass.
  // Without delete ops the Y.Doc keeps the orphans and the next reconcile
  // resurrects them (mirrors mapEdit's prunedChildIds handling).
  const prunedIds = Array.isArray(payload.prunedChildIds)
    ? (payload.prunedChildIds as string[])
    : [];
  prunedIds.forEach((id) => ops.push({ type: "delete-entity", id }));
  return ops;
}

// Broadcast contract for Task 3's pure v2 in-cell move plan/apply
// (`MoveInCellElementPayload`), plus the extra fields the collab mapper needs
// that the Redux apply side doesn't: a `feature` discriminator (routed through
// `create-entity`, matching the `insert-into-cell` precedent) and `fromCellId`
// (to detect a same-cell reorder — see `mapMoveInCellElement`). This is a
// documented handoff point: no Redux dispatch/`createEntity` wiring emits this
// shape yet (that's later-task scope per Task 3's report) — this type is the
// contract a future wiring task should broadcast against.
export type MoveInCellElementBroadcastPayload = MoveInCellElementPayload & {
  feature: "move-in-cell-element";
  fromCellId?: string;
  // Optional text→RichText promotion of the target cell (same fields as
  // insert-into-cell) applied before the reorder on acting + receiving peers.
  promoteEntities?: Record<string, EntityType>;
  promoteRootIds?: string[];
  promoteIndex?: number;
  content?: string;
  priorContent?: string;
  tableData?: unknown;
};

// Cell-scoped (or absolute, for a table -> slide move) analogue of
// getEntityIndex/mapReorder's overId resolution: turns the move's target
// `index` (per MoveInCellElementPayload — position among the target cell's
// OTHER children, as if the moved element were already removed) into a
// reorder-entity `index`. Mirrors resolveAbsoluteIndex in
// apply-in-cell-element-move.ts so the collab op and the local Redux mutation
// agree on where the element lands.
//
// No `overId` is emitted here (unlike mapReorder/mapSwap): reorderEntity's
// overId path re-resolves the anchor's position from `orderedChildren(doc,
// parentId)` — the doc's FULL child list, still including the moving element
// at its old slot — and feeds that raw position straight into
// `computeOrderForIndex`, which expects an index into the list with the
// moving element already excluded. For a single flat list (mapReorder's case)
// that raw/excluded distinction only matters when the anchor sits after the
// moving element, and picking the anchor AT the target slot (rather than
// before it) happens to compensate — see the `dnd-kit`-style "swap into the
// hovered slot" tests in schema.test.ts. But a table's per-cell ordering is
// interleaved with every OTHER cell's children in the SAME global list, so
// which compensation is needed depends on the moving element's CURRENT global
// position relative to the anchor — information this mapper doesn't have
// (and the doc's live order isn't available at mapping time regardless).
// Anchoring on `index` alone and computing it directly against the
// element-excluded list below sidesteps that ambiguity entirely: it exactly
// matches what `computeOrderForIndex` will independently recompute from the
// (assumed not-concurrently-drifted) live doc, regardless of where the moving
// element currently sits.
function resolveInCellMoveAnchor(
  flat: MappingContext["flatContentLayers"],
  toContainerId: string,
  elementId: string,
  toCellId: string | undefined,
  index: number,
): { index: number } {
  const toContainerChildIds = flat?.[toContainerId]?.childIds || [];
  // The container's children with the moving element excluded — exactly the
  // list `computeOrderForIndex` filters down to at apply time. `index` below
  // is resolved as a position WITHIN this list, never the raw (element-
  // included) `toContainerChildIds`.
  const globalSiblings = toContainerChildIds.filter((id) => id !== elementId);
  const cellSiblings =
    toCellId === undefined
      ? globalSiblings
      : globalSiblings.filter(
          (id) => flat?.[id]?.settings?.cellId === toCellId,
        );
  const clamped = Math.max(0, Math.min(index, cellSiblings.length));
  if (clamped < cellSiblings.length) {
    // Insert directly before this cell-sibling's position in the
    // element-excluded global list.
    return { index: globalSiblings.indexOf(cellSiblings[clamped]) };
  }
  // Appending past the cell's last item: no next sibling to anchor "before",
  // so fall back to a slot right after the cell's current last child (or the
  // end of the element-excluded list, for a never-used/empty cell).
  const lastSibling = cellSiblings[cellSiblings.length - 1];
  return {
    index: lastSibling
      ? globalSiblings.indexOf(lastSibling) + 1
      : globalSiblings.length,
  };
}

// v2 in-cell element move (Task 4): a single reorder-entity op repositions
// the element among toContainerId's children and, for a cross-cell move,
// carries settings.cellId in the SAME op's `data` — merged in rather than
// emitted as a second edit-entity op, so this mapping never produces two
// independent writes to the same entity. (Two ops for the same id — one
// touching parentId/order, another touching settings.cellId — could apply
// cleanly here but would still be two separately LWW-resolved writes if a
// concurrent peer's op interleaved with them; merging into one op removes
// that ambiguity entirely.) A same-cell reorder leaves cellId out of `data`
// entirely (no redundant write of the unchanged value).
function mapMoveInCellElement(
  payload: EventPayload,
  context: MappingContext,
): StoryYjsOperation[] | null {
  const {
    elementId,
    toContainerId,
    toCellId,
    fromCellId,
    index,
    promoteEntities,
    promoteRootIds,
    promoteIndex,
    tableData,
  } = payload as MoveInCellElementBroadcastPayload;
  if (!elementId || !toContainerId || typeof index !== "number") return null;

  const ops: StoryYjsOperation[] = [];

  // Promote first so the RichText sibling exists before the moved element's
  // reorder index is resolved against the target cell. The SAME guard gates
  // the flatForAnchor merge below — anchoring against a promotion that never
  // emits ops would compute an index peers can't reproduce.
  const promotion =
    promoteEntities &&
    promoteRootIds?.length &&
    typeof promoteIndex === "number" &&
    toCellId
      ? {
          entities: promoteEntities,
          rootIds: promoteRootIds,
          index: promoteIndex,
        }
      : null;

  if (promotion) {
    promotion.rootIds.forEach((rootId, i) => {
      const root = promotion.entities[rootId];
      if (!root) return;
      ops.push({
        type: "create-entity",
        entity: root,
        entities: collectPayloadSubtree(rootId, promotion.entities),
        parentId: toContainerId,
        index: promotion.index + i,
      });
    });
    if (tableData) {
      ops.push({
        type: "edit-entity",
        id: toContainerId,
        data: { tableData },
      });
    }
  }

  // After promotion, the target cell's sibling list includes the new
  // RichText — resolve the move index against that post-promote flat state
  // by merging promoted entities into a view of context.flatContentLayers.
  const flatForAnchor = promotion
    ? {
        ...context.flatContentLayers,
        ...promotion.entities,
        [toContainerId]: {
          ...context.flatContentLayers?.[toContainerId],
          childIds: (() => {
            const base = [
              ...(context.flatContentLayers?.[toContainerId]?.childIds || []),
            ];
            base.splice(promotion.index, 0, ...promotion.rootIds);
            return base;
          })(),
        },
      }
    : context.flatContentLayers;

  const anchor = resolveInCellMoveAnchor(
    flatForAnchor as MappingContext["flatContentLayers"],
    toContainerId,
    elementId,
    toCellId,
    index,
  );

  const cellIdChanged = toCellId !== fromCellId;
  ops.push({
    type: "reorder-entity",
    id: elementId,
    parentId: toContainerId,
    index: anchor.index,
    overId: null,
    ...(cellIdChanged ? { data: { cellId: toCellId ?? null } } : {}),
  });

  return ops;
}

function mapReorder(
  payload: EventPayload,
  context: MappingContext,
): StoryYjsOperation | null {
  if (payload.type === "swap") return mapSwap(payload, context);
  if (payload.type !== "reorder") return null;
  if (!payload.activeId || !payload.parentId) return null;

  const resolvedIndex = payload.isFirstEntity
    ? 0
    : getEntityIndex(
        context.flatContentLayers,
        payload.parentId,
        payload.overId,
      );

  // A move into an EMPTY target (or any drop whose anchor isn't a current child
  // of the target parent) yields index -1. The Redux reducer performs the move
  // regardless — handleDndParent tolerates a missing overId — so we MUST emit
  // the op too. Dropping it here strands the move in optimistic Redux only, and
  // the next structural reconcile reverts it from the unchanged Y.Doc (the
  // "element snaps back to its previous position" bug). Fall back to an append:
  // index undefined → placed after existing siblings, which for an empty parent
  // is position 0 — exactly where the user dropped it.
  const unresolved = resolvedIndex < 0;
  return {
    type: "reorder-entity",
    id: payload.activeId,
    parentId: payload.parentId,
    index: unresolved ? undefined : resolvedIndex,
    // Carry the drop anchor so the apply side can re-resolve the slot against
    // the doc's live ordering (the index above is computed from the Redux
    // snapshot, which can be a reconcile-microtask stale). An unresolved anchor
    // would never match a live child, so drop it and let the append stand.
    overId: unresolved || payload.isFirstEntity ? null : payload.overId || null,
    // Carry the pointer side so peers resolve the same slot the dispatching
    // client did. Absent for pointerless callers, which keep legacy resolution.
    ...(payload.insertBefore == null
      ? {}
      : { insertBefore: payload.insertBefore as boolean }),
  };
}

function mapSwap(
  payload: EventPayload,
  context: MappingContext,
): StoryYjsOperation | null {
  const flat = context.flatContentLayers;
  const entity = flat?.[payload.id];
  const parentId = entity?.parentId;
  if (!payload.id || !parentId) return null;
  const currentIndex = getEntityIndex(flat, parentId, payload.id);
  const index =
    payload.direction === "forward" ? currentIndex + 1 : currentIndex - 1;
  if (index < 0) return null;
  return { type: "reorder-entity", id: payload.id, parentId, index };
}

function mapUpdateStory(payload: EventPayload): StoryYjsOperation {
  return { type: "update-story", data: payload.data || {} };
}

function mapSetVersionData(payload: EventPayload): StoryYjsOperation | null {
  if (typeof payload.key !== "string" || !payload.key) return null;
  return { type: "set-version-data", key: payload.key, value: payload.value };
}

function mapUpdateStorySettings(payload: EventPayload): StoryYjsOperation {
  return { type: "update-story-settings", data: payload.payload || {} };
}

function mapDesignSystem(payload: EventPayload): StoryYjsOperation[] {
  // Prefer the merged design over the diff: the op writes designSystemV2
  // wholesale, so applying a partial diff would drop every key outside it
  // for all peers.
  const design =
    payload.mergedDesign || payload.design || payload.payload?.design || {};
  const ops: StoryYjsOperation[] = [{ type: "update-design-system", design }];
  // Per-entity results of the design change (RichText content fonts, table
  // cells, element settings). Without these the reducer's local mutations
  // never reach the Y.Doc — peers don't see them, and the next reconcile
  // reverts them for the editing user too.
  const applied = payload.appliedEntities as
    | Record<string, Record<string, unknown>>
    | undefined;
  if (applied && typeof applied === "object") {
    for (const [id, data] of Object.entries(applied)) {
      if (data && Object.keys(data).length > 0) {
        ops.push({ type: "edit-entity", id, data });
      }
    }
  }
  return ops;
}

function mapRestore(payload: EventPayload): StoryYjsOperation | null {
  if (!payload.story) return null;
  return {
    type: "restore-story",
    story: {
      ...payload.story,
      flatContentLayers: payload.flatContentLayers,
    },
  };
}

function mapCustomColors(
  payload: EventPayload,
  context: MappingContext,
): StoryYjsOperation | null {
  // Prefer the post-mutation value carried in the payload — the reducer runs
  // synchronously before React updates the provider's storyRef, so falling
  // back to context.storySettings would write the stale pre-mutation array.
  const customColors = Array.isArray(payload?.customColors)
    ? payload.customColors
    : (context.storySettings?.customColors as unknown[] | undefined);
  if (!customColors) return null;
  return {
    type: "update-story-settings",
    data: { customColors },
  };
}

function mapAnchors(
  payload: EventPayload,
  context: MappingContext,
): StoryYjsOperation | null {
  const anchors =
    payload?.anchors && typeof payload.anchors === "object"
      ? payload.anchors
      : (context.storySettings?.anchors as Record<string, unknown> | undefined);
  if (!anchors) return null;
  return {
    type: "update-story-settings",
    data: { anchors },
  };
}

function getEntityIndex(
  flat: MappingContext["flatContentLayers"],
  parentId: string,
  id: string,
) {
  const parent = flat?.[parentId];
  return parent?.childIds?.indexOf(id) ?? -1;
}

// A combo/slide layout switch dispatches changeEntityLayout with the full
// before/after subtrees: { parentId, original, changed, type }. Normalize the
// new subtree to flat entities (matching what the reducer writes to Redux),
// diff against the old one to find dropped entities, and emit a structural
// change-layout op. A legacy single-entity { id, data } shape is still mapped
// to a plain edit for back-compat with any in-flight broadcasts.
function mapChangeLayout(payload: EventPayload): StoryYjsOperation | null {
  if (payload.id && payload.data) {
    return {
      type: "edit-entity",
      id: payload.id,
      data: payload.data,
      full: payload.full,
      children: payload.children,
    };
  }

  const { parentId, original, changed, type } = payload;
  if (!changed || (type !== "combo" && type !== "slide")) return null;

  const next = normalizeLayoutSubtree(changed, type, parentId);
  const rootId = next.root;
  const newEntities = next.entities as StorydocFlatContentLayers;
  if (!rootId || !newEntities[rootId]) return null;

  const prevEntities = original
    ? (normalizeLayoutSubtree(original, type, parentId)
        .entities as StorydocFlatContentLayers)
    : {};
  const removedIds = Object.keys(prevEntities).filter((id) => !newEntities[id]);

  return {
    type: "change-layout",
    rootId,
    parentId: type === "slide" ? "root" : parentId,
    entities: newEntities,
    removedIds,
  };
}

// Mirror exactly how the changeEntityLayout reducer normalizes the subtree, so
// the Y.Doc and Redux agree on ids, parentage and childIds (keepIds: true).
function normalizeLayoutSubtree(
  root: Record<string, unknown>,
  type: "combo" | "slide",
  parentId: string,
) {
  return type === "slide"
    ? normalizedFromNestedStructured(
        [root],
        "root",
        "slide",
        undefined,
        undefined,
        true,
      )
    : normalizedFromNestedStructured(
        [{ children: [root] }],
        parentId,
        "combo",
        undefined,
        undefined,
        true,
      );
}
